import { bind } from 'bind-decorator';
import { focusElement } from 'oaf-side-effects';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { MessageDescriptor } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import * as LoginActions from 'auth/actions';
import { getUsername } from 'auth/selectors';
import { ROUTES } from 'global/constants';
import { RootState } from 'global/state';
import { LXThunkUnwrap } from 'global/types';
import { getNotificationCount } from 'notifications/selectors';
import { UI_IS_SM } from 'ui/breakpoints';
import { Footer, Header, HeaderBackgroundStyle } from 'ui/components';
import messages from 'ui/components/displayMessages';
import { WrappedMessage } from 'utils';
import * as UIActions from '../../actions';
import { getSidebarVisibility } from '../../selectors';
import { MobileSidebar } from '../MobileSidebar';
import { BannerCookieAcceptance } from 'banners/components/CookieAcceptance';
import { intl } from 'i18n';

interface State {
    isMobileView: boolean;
}

interface OwnProps {
    // Own props are any props that are _not_ derived from the Redux state or auto-generated
    pageTitle?: MessageDescriptor;
    pageTitleValues?: {[key: string]: string};
    children: React.ReactNode;
    headerFeature?: React.ReactNode;
    // Should the "header feature" be placed into a container (made the same width as the
    // content, and not full screen width). Default: false.
    headerFeatureContainer?: boolean;
    headerBackgroundUrl?: string|null;
    // For classroom pages, the background should be centered and faded, this toggle enables that
    backgroundClassrooms?: boolean;
    backgroundExplore?: boolean;
    // Additional content above the main content element (not within it), but below the header
    topContent?: React.ReactNode;
    // Additional content below the main content element (not within it)
    bottomContent?: React.ReactNode;
    // Normally, our main content area uses either the 'container' or 'container-fluid'
    // Bootstrap grid setting, depending on the display size (fluid on mobile).
    // If you need to override that for a particular page, you can set this to one or
    // the other.
    containerClassName?: 'container' | 'container-fluid' | 'none';
    backgroundStyle?: 'navy-gradient' | 'light-header';
    // Classes to apply to the main element.
    mainClassName?: string;
    // Additional class name(s) to apply to the main content element.
    otherClassName?: string;
    containerPaddingEnabled?: boolean;
    headerContainerPaddingEnabled?: boolean;
    footerFeature?: React.ReactNode;
    searchUrl?: string;
    searchParams?: string[];
}

interface ActionProps {
    loginUser: typeof LoginActions.redirectToLogin;
    logoutUser: typeof LoginActions.logout;
    showSidebar: typeof UIActions.showSidebar;
    hideSidebar: typeof UIActions.hideSidebar;
}

interface StateProps {
    username: string;
    sidebarVisible: boolean;
    notificationCount: number;
}

interface Props extends StateProps, OwnProps, LXThunkUnwrap<ActionProps>, RouteComponentProps<{}> {
}

class StandardPageLayoutInternal extends React.Component<Props, State> {

    public static defaultProps = {
        containerPaddingEnabled: true,
        headerContainerPaddingEnabled: true,
    };

    private mediaQuery = UI_IS_SM;  // This matches Bootstrap's definition of small (sm) breakpoint.

    constructor(props: any) {
        super(props);

        this.state = {
            isMobileView: false,
        };
    }

    public componentDidMount() {
        this.mediaQuery.addListener(this.setIsMobileView);
        this.setIsMobileView();
    }

    public componentWillUnmount() {
        this.mediaQuery.removeListener(this.setIsMobileView);
    }

    public render() {
        let headerBackgroundStyle: HeaderBackgroundStyle = 'dark';

        let backgroundClassName;
        if (this.props.backgroundStyle === 'navy-gradient') {
            headerBackgroundStyle = 'transparent';
            backgroundClassName = 'page-background-navy-gradient';
        } else if (this.props.backgroundStyle === 'light-header') {
            headerBackgroundStyle = 'light';
            backgroundClassName = this.props.backgroundStyle;
        }

        // CSS classes to apply to the main container <div>, in addition to .main-content
        const containerClass = (
            this.props.containerClassName ? this.props.containerClassName :
            this.state.isMobileView ? 'container-fluid' :
            'container'
        ) + (
            this.props.containerPaddingEnabled ? ' pt-3 pb-5' : ''
        );

        // CSS classes to apply to the header feature container
        const headerContainerClass = (
            this.state.isMobileView ? 'container-fluid' : 'container'
        ) + (
            this.props.headerContainerPaddingEnabled ? ' pt-3 pb-5' : ''
        );

        return (
            <div className={'layout-wrapper d-flex flex-column ' + (backgroundClassName ? backgroundClassName : '')}>
                <Helmet>
                    <title>{this.pageTitle()}</title>
                </Helmet>
                {this.props.children &&
                    <a href='#main-content' className='skip-link' onClick={this.skipToMainContent}>
                        <WrappedMessage message={messages.skipLinkText} />
                    </a>
                }
                <Header
                    backgroundStyle={headerBackgroundStyle}
                    backgroundUrl={this.props.headerBackgroundUrl}
                    backgroundClassrooms={this.props.backgroundClassrooms}
                    backgroundExplore={this.props.backgroundExplore}
                    username={this.props.username}
                    onLogIn={this.handleLogin}
                    onLogOut={this.handleLogout}
                    onSignup={this.handleSignup}
                    notificationCount={this.props.notificationCount}
                    onShowSidebar={this.props.showSidebar}
                    searchUrl={(this.props.searchUrl || ROUTES.Library.HOME)}
                    searchParams={this.props.searchParams || []}
                >
                    {this.props.headerFeatureContainer ?
                        <div className={headerContainerClass}>{this.props.headerFeature}</div>
                    :
                        this.props.headerFeature
                    }
                </Header>
                <MobileSidebar
                    visible={this.props.sidebarVisible}
                    onHideSidebar={this.props.hideSidebar}
                    notificationCount={this.props.notificationCount}
                    username={this.props.username}
                    onLogIn={this.handleLogin}
                    onSignup={this.handleSignup}
                    onLogOut={this.handleLogout}
                />
                <main className={this.props.mainClassName}>
                    {this.props.topContent &&
                        <div className='top-content'>{this.props.topContent}</div>
                    }
                    {this.props.children &&
                        <div id='main-content'
                             className={`main-content ${containerClass} ${this.props.otherClassName || ''}`}>
                            {this.props.children}
                        </div>
                    }
                    {this.props.bottomContent &&
                        <div className='bottom-content'>{this.props.bottomContent}</div>
                    }
                </main>
                <Footer backgroundUrl='assets/images/footer-background.svg'
                        footerFeature={this.props.footerFeature ? this.props.footerFeature : null}/>
                <BannerCookieAcceptance />
            </div>
        );
    }

    private pageTitle() {
        if (this.props.pageTitle) {
            const title = intl.formatMessage(this.props.pageTitle, this.props.pageTitleValues);
            return intl.formatMessage(messages.pageTitlePlaceholder, {title});
        }
        return intl.formatMessage(messages.defaultPageTitle);
    }

    private handleLogin = () => {
        LoginActions.redirectToLogin();
    }

    private handleLogout = () => {
        LoginActions.logout();
    }

    private handleSignup = () => {
        this.props.history.push(ROUTES.General.SIGN_UP);
    }

    @bind private setIsMobileView() {
        this.setState({isMobileView: this.mediaQuery.matches});
    }

    @bind private skipToMainContent(event: React.MouseEvent) {
        event.preventDefault();
        focusElement('#main-content');
    }
}

// Unfortunately this connect() isn't working as a decorator at the moment
export const StandardPageLayout = connect<StateProps, ActionProps, OwnProps, RootState>(
    (state: RootState) => ({
        sidebarVisible: getSidebarVisibility(state),
        username: getUsername(state),
        notificationCount: getNotificationCount(state),
    }), {
        hideSidebar: UIActions.hideSidebar,
        loginUser: LoginActions.redirectToLogin,
        logoutUser: LoginActions.logout,
        showSidebar: UIActions.showSidebar,
    },
)(withRouter(StandardPageLayoutInternal));
